import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { Icon } from '../lib/icons.tsx';
import { CategoryLabel } from './CategoryLabel.tsx';
import { navigate, type Route } from '../lib/router.ts';
import { ServiceSearchBox } from './ServiceSearchBox.tsx';
import { LOGO_URL } from '../lib/assets.ts';

interface HeaderProps {
  route: Route;
  favs: string[];
}

export function Header({ route, favs }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const cats = useQuery({
    queryKey: qk.categories.list(),
    queryFn: () => api.listCategories(),
  });

  // 데스크톱 폭으로 리사이즈되면 drawer 닫기 (스크롤 잠금 잔존 방지).
  useEffect(() => {
    const mq = window.matchMedia('(min-width:761px)');
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 라우트가 바뀌면 (drawer 항목 탭 포함) 모바일 메뉴 닫기.
  useEffect(() => {
    setMenuOpen(false);
  }, [route]);

  // drawer 열린 동안 바디 스크롤 잠금 + Esc 닫기.
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const go = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };

  const catItems = cats.data?.items ?? [];

  return (
    <header className={`nf-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="nf-header-inner">
        <button
          className="nf-icon-btn nf-hamburger"
          aria-label="메뉴 열기"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <div className="nf-logo" onClick={() => navigate('/')} style={{ cursor: 'default' }}>
          <img src={LOGO_URL} className="nf-logo-img" alt="" width={32} height={22} />
          <span>
            <span className="n">n</span>ewflix
          </span>
        </div>
        <nav className="nf-nav">
          <div
            className={`item ${route.page === 'landing' ? 'active' : ''}`}
            onClick={() => navigate('/')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon.home /> 홈
          </div>
          <div
            className="item"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon.grid /> 카테고리 <Icon.chevron />
            {catOpen && (
              <div className="nf-menu" onMouseLeave={() => setCatOpen(false)}>
                {catItems.map((c) => (
                  <div
                    key={c.slug}
                    className="nf-menu-item"
                    onClick={() => navigate('/c/' + encodeURIComponent(c.slug))}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <CategoryLabel slug={c.slug} name={c.name} color={c.color} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={`item ${route.page === 'collections' ? 'active' : ''}`}
            onClick={() => navigate('/collections')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon.bookmark /> 컬렉션
          </div>
          <div
            className={`item ${route.page === 'requests' ? 'active' : ''}`}
            onClick={() => navigate('/requests')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon.plus /> 서비스 요청
          </div>
        </nav>
        <ServiceSearchBox onSelect={(s) => navigate('/s/' + s.slug)} />
        <button
          className="nf-icon-btn nf-fav-btn"
          onClick={() => navigate('/favorites')}
          title="즐겨찾기"
        >
          <Icon.heart />
          {favs.length > 0 && <span className="badge" />}
        </button>
        <button className="nf-icon-btn" onClick={() => navigate('/me')} title="마이">
          <Icon.user />
        </button>
      </div>

      {menuOpen && (
        <>
          <div className="nf-drawer-backdrop" onClick={() => setMenuOpen(false)} />
          <aside className="nf-drawer" role="dialog" aria-modal="true" aria-label="메뉴">
            <div className="nf-drawer-head">
              <div className="nf-logo" onClick={() => go('/')} style={{ cursor: 'default' }}>
                <img src={LOGO_URL} className="nf-logo-img" alt="" width={32} height={22} />
                <span>
                  <span className="n">n</span>ewflix
                </span>
              </div>
              <button className="nf-icon-btn" aria-label="메뉴 닫기" onClick={() => setMenuOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>

            <ServiceSearchBox onSelect={(s) => go('/s/' + s.slug)} />

            <nav className="nf-drawer-nav">
              <button
                className={`nf-drawer-item ${route.page === 'landing' ? 'active' : ''}`}
                onClick={() => go('/')}
              >
                <Icon.home /> 홈
              </button>
              <button
                className={`nf-drawer-item ${route.page === 'collections' ? 'active' : ''}`}
                onClick={() => go('/collections')}
              >
                <Icon.bookmark /> 컬렉션
              </button>
              <button
                className={`nf-drawer-item ${route.page === 'requests' ? 'active' : ''}`}
                onClick={() => go('/requests')}
              >
                <Icon.plus /> 서비스 요청
              </button>
              <button className="nf-drawer-item" onClick={() => go('/favorites')}>
                <Icon.heart /> 즐겨찾기
                {favs.length > 0 && <span className="nf-drawer-count">{favs.length}</span>}
              </button>
              <button className="nf-drawer-item" onClick={() => go('/me')}>
                <Icon.user /> 마이
              </button>
            </nav>

            <div className="nf-drawer-section">
              <div className="nf-drawer-section-title">
                <Icon.grid /> 카테고리
              </div>
              <div className="nf-drawer-cats">
                {catItems.map((c) => (
                  <button
                    key={c.slug}
                    className="nf-drawer-cat"
                    onClick={() => go('/c/' + encodeURIComponent(c.slug))}
                  >
                    <CategoryLabel slug={c.slug} name={c.name} color={c.color} />
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </>
      )}
    </header>
  );
}
